import "./provider-notice.js";
import { installAtlasExplorer } from "./atlas-explorer.js";
import { renderPortraitCredit } from "./portrait-rendering.js";
import { inspectorPortraitPeople, renderRelationPortraits } from "./relation-portraits.js";
import { atlasNodeProgram } from "./atlas-node-program.js";
import { installAtlasPerformance } from "./atlas-performance.js";
import { installCommunityLayer } from "./community-layer.js";
import { createSemanticZoom } from "./semantic-zoom.js";
import { atlasEdgeProgram } from "./atlas-edge-program.js";
import { validWarmLayout } from "./warm-layout.js";
import { ComponentView } from "./component-view.js";
import { installGraphLoading } from "./graph-loading.js";
import { installRelationInteraction } from "./relation-hit.js";
import { shortestPaths, pathColor } from "./shortest-paths.js";
import { installPathSearch } from "./path-search.js";
import { mobileNodeSize, mobileEdgeSize } from "./mobile-graph.js";
import { installMobileSearch } from "./mobile-search.js";
import { MultiGraph } from "graphology";
import Sigma from "sigma";
import { StableLayout } from "./stable-layout.js";
import { installNodeDrag } from "./node-drag.js";
import { installLayoutControls } from "./layout-controls.js";
import { loadLayoutSettings } from "./layout-settings.js";
import { parseView, serializeView } from "./view-state.js";
import { personConnections } from "./person-connections.js";
import { installViewChrome } from "./view-chrome.js";
import { installShareView } from "./share-view.js";
import { installGraphLabels } from "./graph-labels.js";
import { installDiscovery } from "./discovery.js";

const mobileViewport = matchMedia("(max-width: 760px)");
const semantic = createSemanticZoom(() => mobileViewport.matches);
const graph = new MultiGraph({ allowSelfLoops: false });
let layoutSettings;
try { layoutSettings = loadLayoutSettings(localStorage); } catch { layoutSettings = loadLayoutSettings(null); }
const initialView = location.search;
layoutSettings = parseView(initialView, { fallbackSettings: layoutSettings }).layoutSettings;
let viewReady = false, urlTimer;
const container = document.querySelector("#graph");
const graphLoading = installGraphLoading(document);
let renderer;
try {
  renderer = new Sigma(graph, container, {
    renderEdgeLabels: false,
    // Do not render thousands of overlapping seed positions while loading.
    nodeReducer: (_node, data) => ({ ...data, hidden: true }),
    edgeReducer: (_edge, data) => ({ ...data, hidden: true }),
    nodeProgramClasses: { circle: atlasNodeProgram(() => mobileViewport.matches) },
    zoomToSizeRatioFunction: ratio => semantic.sizeRatio(ratio),
    hideEdgesOnMove: false,
    edgeProgramClasses: { line: atlasEdgeProgram(semantic), focus: atlasEdgeProgram(semantic, true) },
    enableEdgeEvents: true,
    labelSize: Math.max(10, 14 * Math.sqrt(layoutSettings.displayScale)),
    labelRenderedSizeThreshold: 4.5,
    labelFont: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    labelColor: { color: "#f2efe8" },
    defaultEdgeColor: "#596260",
    defaultNodeColor: "#a6afad",
    zIndex: true,
    stagePadding: 12,
    minEdgeThickness: mobileViewport.matches ? .8 : 1,
    antiAliasingFeather: .5,
    minCameraRatio: .02,
    maxCameraRatio: 30,
  });
} catch (error) {
  graphLoading.failed();
  throw error;
}

semantic.attach(renderer.getCamera());
const labelAudit = installGraphLabels(renderer, graph, semantic);
labelAudit.inspectorPeople=()=>{
  const panel=document.querySelector("#inspector");
  return inspectorPortraitPeople(graph,state,Boolean(panel&&!panel.hidden));
};
labelAudit.onPortraitChange = qid => {
  if (state.selectedNode && graph.hasNode(state.selectedNode) && graph.getNodeAttribute(state.selectedNode,"wikidataId") === qid) refreshPortraitCredit();
  if (state.selectedEdge && graph.hasEdge(state.selectedEdge) && graph.extremities(state.selectedEdge).some(id=>graph.getNodeAttribute(id,"wikidataId")===qid)) refreshRelationPortraits();
};
const communities = installCommunityLayer(renderer, graph, semantic, () => { labelAudit.setCommunities(communities); explorer?.groupsChanged(); applyFilters(); });
const performanceAudit = installAtlasPerformance(renderer, graph);
const canvases = renderer.getCanvases();
container.insertBefore(canvases.edges, canvases.hovers);
let discovery, explorer;
let hoverNode = null, hoverNeighbors = new Set(), hoverEdges = new Set();
let hoverClearTimer = null, hoverPointer = null;
container.style.opacity = "0";

const domainColors = {
  CULTURE: "#f0ad5b",
  POLITIQUE: "#43d7cf",
  MEDIA: "#b49cff",
  ECONOMIE: "#8fd17f",
  RELATION: "#e88975",
};
const professionDomainRules = {
  CULTURE: /acteur|actrice|cinéaste|réalisat|scénar|écrivain|écrivaine|auteur|autrice|romanci|chanteu|chanteuse|musicien|compositeur|artiste|photograph|metteur en scène|mannequin|producteur de cinéma|productrice de cinéma|dramaturge|humoriste|danseu|chorégraphe|designer|architecte|peintre|sculpteur/,
  POLITIQUE: /politique|homme d’état|femme d’état|ministre|député|sénateur|maire|président de la république|conseiller régional|conseillère régionale|conseiller départemental|conseillère départementale|diplomate|haut fonctionnaire|haute fonctionnaire|secrétaire d’état|gouverneur|parlementaire/,
  MEDIA: /journaliste|animateur|animatrice|présentateur|présentatrice|chroniqueur|chroniqueuse|reporter|éditorialiste|producteur de télévision|productrice de télévision|producteur de radio|productrice de radio|média|patron de presse|éditrice|éditeur/,
  ECONOMIE: /entrepreneur|entrepreneuse|chef d’entreprise|dirigeant d’entreprise|dirigeante d’entreprise|homme d’affaires|femme d’affaires|industriel|industrielle|banquier|banquière|financier|financière|investisseur|investisseuse|économiste|président-directeur général|directeur général|directrice générale|magnat|monde des affaires/,
};
const relationLabels = {
  SPOUSE: "Conjoints", PARTNER: "Couple", PARENT_OF: "Parent de", ADOPTIVE_PARENT_OF: "Parent adoptif de", STEP_PARENT_OF: "Beau-parent de", CO_PARENT: "Coparents", SIBLING: "Fratrie",
  OTHER_FAMILY: "Autre lien familial", COLLABORATOR: "Collaboration (V1)",
  BUSINESS_PARTNER: "Associés (V1)", FRIEND: "Amitié explicite (V1)",
  CO_FOUNDED_WITH: "Cofondation", CO_LED_WITH: "Codirection",
  CO_AUTHORED_WITH: "Coécriture", MENTOR_OF: "Mentorat",
  COLLABORATED_CLOSELY_WITH: "Collaboration étroite",
};
const relationDescriptions = {
  SPOUSE: "Mariage explicitement documenté entre ces deux personnes.",
  PARTNER: "Relation de couple explicitement documentée.",
  PARENT_OF: "Lien de filiation documenté, orienté du parent vers l’enfant.",
  ADOPTIVE_PARENT_OF: "Filiation adoptive explicitement documentée, orientée du parent vers l’enfant.",
  STEP_PARENT_OF: "Conjoint ou partenaire d’un parent, actuel ou passé ; distinct de la filiation biologique, de l’adoption et des parents du conjoint.",
  CO_PARENT: "Parents d’un même enfant ; ce lien ne signifie ni mariage ni couple.",
  SIBLING: "Lien de fratrie explicitement documenté.",
  OTHER_FAMILY: "Autre lien familial explicitement documenté.",
  COLLABORATOR: "Relation professionnelle conservée depuis le schéma V1.",
  BUSINESS_PARTNER: "Partenariat d’affaires conservé depuis le schéma V1.",
  FRIEND: "Amitié explicite conservée depuis le schéma V1.",
  CO_FOUNDED_WITH: "Cofondation explicite d’une même organisation, entreprise, média, mouvement ou institution.",
  CO_LED_WITH: "Direction commune explicite et durable d’une même structure.",
  CO_AUTHORED_WITH: "Coécriture explicite d’une œuvre ou publication signée.",
  MENTOR_OF: "Relation de mentorat documentée, orientée du mentor vers la personne accompagnée.",
  COLLABORATED_CLOSELY_WITH: "Collaboration durable et significative explicitement décrite.",
};
const temporalLabels = { CURRENT: "Actuelle", PAST: "Passée", UNKNOWN: "Non précisée" };
const relationLabel = edge => edge.type === "PARTNER" && edge.temporalStatus === "PAST"
  ? "Ancien couple" : (relationLabels[edge.type] || edge.type);
const extractionLabels = {
  WIKIDATA_STRUCTURED: "Donnée structurée Wikidata",
  WIKIPEDIA_INFOBOX: "Champ structuré de l’infobox",
  TYPESAFE_SYSTEM_ONE: "Passage Wikipédia qualifié par JEV",
};
const professionLabels = new Map();
const state = {
  pathIds: [],
  sequence: 0,
  projected: false, epoch: null, collectedPeople: 0, collectedRelations: 0,
  initialWarm: null, warmPositions: new Map(),
  paused: true,
  selectedNode: null,
  selectedEdge: null,
  domains: new Set(),
  relationType: "ALL",
  eventSource: null,
  layout: null,
  reconnectTimer: null,
};

let pathResult = shortestPaths(graph, []), pathsDirty = false, pathTimer;
function refreshPaths() {
  clearTimeout(pathTimer); pathsDirty = false;
  pathResult = shortestPaths(graph, state.pathIds);
  labelAudit.setPaths(state.pathIds.length ? pathResult : null);
  pathSearch.update(pathResult);
}
for (const event of ["nodeAdded", "nodeDropped", "edgeAdded", "edgeDropped", "cleared", "edgesCleared"]) {
  graph.on(event, () => {
    pathsDirty = true;
    if (state.pathIds.length) { clearTimeout(pathTimer); pathTimer = setTimeout(() => { refreshPaths(); applyFilters(); }, 80); }
  });
}
function setPathIds(ids) {
  if (ids.length) discovery?.takeControl();
  if (ids.length) explorer?.paths(ids);
  state.pathIds = ids;
  container.style.cursor = "default";
  if (ids.length) {
    state.domains.clear(); state.relationType = "ALL";
    state.selectedNode = null; state.selectedEdge = null;
    document.querySelector("#inspector").hidden = true;
  }
  document.querySelectorAll(".domain").forEach(button => {
    button.disabled = ids.length > 0;
    button.classList.toggle("is-on", state.domains.has(button.dataset.domain));
    button.setAttribute("aria-pressed", String(state.domains.has(button.dataset.domain)));
  });
  document.querySelector("#relationFilter").disabled = ids.length > 0;
  document.querySelector("#relationFilter").value = state.relationType;
  refreshPaths(); explorer?.pathsReady(); applyFilters();
}
function fitPaths() {
  const ids = [...new Set([...state.pathIds, ...pathResult.nodes.keys()])].filter(id => graph.hasNode(id));
  if (!ids.length) return;
  renderer.getCamera().setState({ angle: 0 });
  const pixels = ids.map(id => renderer.graphToViewport(graph.getNodeAttributes(id)));
  const points = pixels.map(p => renderer.viewportToFramedGraph(p));
  const xs = points.map(p => p.x), ys = points.map(p => p.y);
  const x = (Math.min(...xs) + Math.max(...xs)) / 2, y = (Math.min(...ys) + Math.max(...ys)) / 2;
  const px = pixels.map(p => p.x), py = pixels.map(p => p.y);
  const { width, height } = renderer.getDimensions();
  // Use actual projected pixels: Sigma's aspect-ratio correction is not
  // simply min(width,height), especially on a portrait mobile viewport.
  const ratio = Math.max(.12, Math.min(30, renderer.getCamera().getState().ratio * Math.max(
    (Math.max(...px) - Math.min(...px)) / Math.max(120, width - (mobileViewport.matches ? 90 : 600)),
    (Math.max(...py) - Math.min(...py)) / Math.max(120, height - 240)) * 1.2));
  renderer.getCamera().setState({ x, y, ratio, angle: 0 });
}
function deterministicPosition(id) {
  let hash = 2166136261;
  for (const character of id) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  const angle = (hash >>> 0) / 4294967296 * Math.PI * 2;
  const radius = 20 + ((hash >>> 8) % 100);
  return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
}

function upsertNode(person) {
  const existing = graph.hasNode(person.id);
  const position = existing ? { x: graph.getNodeAttribute(person.id, "x"), y: graph.getNodeAttribute(person.id, "y") }
    : state.warmPositions.get(person.id) || positionNearKnownNeighbor(person.id);
  const attributes = {
    ...person,
    label: person.name,
    color: "#89918f",
    careerDomains: inferCareerDomains(person),
    size: existing ? graph.getNodeAttribute(person.id, "size") || 5 : (person.role === "ELITE" ? 7 : 4.5),
    x: position.x,
    y: position.y,
  };
  if (existing) graph.mergeNodeAttributes(person.id, attributes);
  else graph.addNode(person.id, attributes);
}

function upsertEdge(edge) {
  const attributes = {
    ...edge,
    relationType: edge.type,
    label: relationLabel(edge),
    size: Math.min(1.3 + Math.log2(1 + (edge.evidenceCount || 1)), 4),
    color: "#68716f",
    type: "line",
  };
  if (graph.hasEdge(edge.id)) graph.mergeEdgeAttributes(edge.id, attributes);
  else if (edge.type === "PARENT_OF" || edge.type === "ADOPTIVE_PARENT_OF" || edge.type === "STEP_PARENT_OF" || edge.type === "MENTOR_OF") {
    graph.addDirectedEdgeWithKey(edge.id, edge.sourcePersonId, edge.targetPersonId, attributes);
  } else graph.addUndirectedEdgeWithKey(edge.id, edge.sourcePersonId, edge.targetPersonId, attributes);
}

let viewAdjacency = new Map();
function positionNearKnownNeighbor(id) {
  for (const neighbor of viewAdjacency.get(id) || []) {
    if (graph.hasNode(neighbor)) {
      const base = graph.getNodeAttributes(neighbor), offset = deterministicPosition(id);
      return { x: base.x + offset.x * .3, y: base.y + offset.y * .3 };
    }
  }
  return deterministicPosition(id);
}

function updateNodeSizes(...ids) {
  for (const id of ids) {
    if (!graph.hasNode(id)) continue;
    const degree = graph.degree(id);
    const base = graph.getNodeAttribute(id, "role") === "ELITE" ? 6 : 4;
    graph.setNodeAttribute(id, "size", Math.min(base + Math.sqrt(degree) * 1.5, 14));
  }
}

function syncLayout() {
  if (!state.layout) {
    state.layout = new StableLayout(graph, renderer, () => {
      semantic.setInitial(renderer.getCamera().getState().ratio);
      labelAudit.setOverviewRatio(renderer.getCamera().getState().ratio);
      applyFilters();
      container.style.opacity = "1";
      graphLoading.ready();
      document.querySelector("#app").setAttribute("aria-busy", "false");
      explorer?.ready();
      restoreViewFromUrl();
      document.dispatchEvent(new Event("entourages:people-updated"));
      discovery?.ready();
    }, layoutSettings, progress => graphLoading.arranging(progress));
    state.layout.paused = state.paused;
    state.layout.worker.addEventListener("error", () => graphLoading.failed());
    if (state.initialWarm) state.layout.prime(state.initialWarm);
    state.initialWarm = null; state.warmPositions.clear();
  }
  graphLoading.arranging();
  state.layout.sync();
  document.documentElement.dataset.forceAtlasWorker = state.paused ? "paused" : "running";
}

const collectedView = new ComponentView(1, false);
let displayedNodes = new Map(), displayedEdges = new Map(), viewTimer = null;
let resyncing = false;
// Yield during first insertion, but apply each live batch atomically before paint.
function* reconcileView() {
  const view = collectedView.project();
  const nodeIds = new Set(view.nodes.map(n => n.id)), edgeIds = new Set(view.edges.map(e => e.id));
  const changedNodes = view.nodes.filter(n => displayedNodes.get(n.id) !== n);
  const changedEdges = view.edges.filter(e => displayedEdges.get(e.id) !== e);
  const removedEdges = graph.edges().filter(id => !edgeIds.has(id));
  const removedNodes = graph.nodes().filter(id => !nodeIds.has(id));
  const topologyChanged = removedNodes.length || removedEdges.length || view.nodes.some(n => !graph.hasNode(n.id)) || view.edges.some(e => !graph.hasEdge(e.id));
  for (const id of removedEdges) graph.dropEdge(id);
  for (const id of removedNodes) graph.dropNode(id);
  // A corrected endpoint/type is a topology change, not just an attribute update.
  let rewired = false;
  for (const edge of changedEdges) if (graph.hasEdge(edge.id)) {
    const old = displayedEdges.get(edge.id);
    if (old.sourcePersonId !== edge.sourcePersonId || old.targetPersonId !== edge.targetPersonId || old.type !== edge.type) {
      graph.dropEdge(edge.id); rewired = true;
    }
  }
  viewAdjacency = view.adjacency;
  const total = changedNodes.length + changedEdges.length;
  let done = 0;
  for (const node of changedNodes) { upsertNode(node); if (++done % 200 === 0) yield { done, total }; }
  for (const edge of changedEdges) { upsertEdge(edge); if (++done % 200 === 0) yield { done, total }; }
  if (topologyChanged || rewired || changedNodes.length) updateNodeSizes(...nodeIds);
  displayedNodes = new Map(view.nodes.map(n => [n.id, n]));
  displayedEdges = new Map(view.edges.map(e => [e.id, e]));
  if ((state.selectedNode && !graph.hasNode(state.selectedNode)) || (state.selectedEdge && !graph.hasEdge(state.selectedEdge))) {
    state.selectedNode = null; state.selectedEdge = null;
    document.querySelector("#inspector").hidden = true;
  }
  if (changedNodes.length || removedNodes.length) updateSearchList();
  else if(changedEdges.length || removedEdges.length) document.dispatchEvent(new Event("entourages:people-updated"));
  if (changedNodes.length || changedEdges.length || removedNodes.length || removedEdges.length) applyFilters();
  if (!state.layout || topologyChanged || rewired) syncLayout();
  refreshPersonConnections();
  for (const note of document.querySelectorAll("[data-component-view-note]")) note.textContent = `${graph.order.toLocaleString("fr-FR")} personnes disponibles dans le répertoire, avec ou sans lien documenté.`;
  void hydrateCareerDomains(changedNodes);
  yield { done: total, total };
}
function flushView() {
  clearTimeout(viewTimer); viewTimer = null;
  for (const ignored of reconcileView()) { /* One batched live update. */ }
}
async function applySnapshot(snapshot) {
  clearTimeout(viewTimer); viewTimer = null;
  state.projected = typeof snapshot.epoch === "string";
  state.epoch = snapshot.epoch || null;
  state.collectedPeople = snapshot.collected?.people ?? snapshot.nodes.length;
  state.collectedRelations = snapshot.collected?.relations ?? snapshot.edges.length;
  collectedView.replace(snapshot);
  const visibleIds = new Set(collectedView.project().nodes.map(n => n.id));
  const visibleLayout = Array.isArray(snapshot.layout?.rows)
    ? { ...snapshot.layout, rows: snapshot.layout.rows.filter(r => Array.isArray(r) && visibleIds.has(r[0])) } : null;
  if (!state.layout && validWarmLayout(visibleLayout, visibleIds, layoutSettings)) {
    // Share exactly the same cached coordinates on desktop and mobile.
    state.initialWarm = visibleLayout;
    state.warmPositions = new Map(state.initialWarm.rows.map(r => [r[0], {x:r[1],y:r[2]}]));
  }
  graphLoading.preparing(0);
  await new Promise(resolve => setTimeout(resolve, 0));
  for (const { done, total } of reconcileView()) {
    graphLoading.preparing(total ? done / total : 1);
    await new Promise(resolve => setTimeout(resolve, 0));
  }
  state.sequence = snapshot.sequence;
  if (snapshot.status) updateCounts(snapshot.status);
  else {
    setText("#peopleCount", state.collectedPeople.toLocaleString("fr-FR"));
    setText("#relationCount", state.collectedRelations.toLocaleString("fr-FR"));
  }
  graphLoading.arranging(0);
}
function applyEvent(event) {
  if (resyncing || !event || event.sequence <= state.sequence) return;
  if (event.sequence !== state.sequence + 1) { void resyncAndConnect(); return; }
  state.sequence = event.sequence;
  if(event.type === "EDGE_UPSERT" && !collectedView.edges.has(event.payload.id)) explorer?.received(event.payload.id);
  if (collectedView.apply(event.type, event.payload)) {
    state.collectedPeople = collectedView.nodes.size; state.collectedRelations = collectedView.edges.size;
    if (viewTimer === null) viewTimer = setTimeout(flushView, 80);
  }
}
async function resyncAndConnect() {
  if (resyncing) return;
  resyncing = true;
  graphLoading.fetching();
  clearTimeout(viewTimer); viewTimer = null;
  if (state.reconnectTimer) clearTimeout(state.reconnectTimer);
  if (state.eventSource) { state.eventSource.close(); state.eventSource = null; }
  try {
    const response = await fetch("api/v1/snapshot", { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(30000) });
    if (!response.ok) throw new Error(`snapshot ${response.status}`);
    await applySnapshot(await response.json());
    connectEvents();
  } catch (error) {
    graphLoading.retrying();
    showToast("Réseau momentanément indisponible");
    state.reconnectTimer = setTimeout(resyncAndConnect, 3000);
  } finally { resyncing = false; }
}

function connectEvents() {
  if (state.eventSource) state.eventSource.close();
  const url = state.projected ? `api/v1/view/events/stream?epoch=${encodeURIComponent(state.epoch)}&since=${state.sequence}`
    : `api/v1/events/stream?since=${state.sequence}`;
  const stream = new EventSource(url);
  state.eventSource = stream;
  stream.addEventListener(state.projected ? "view" : "graph", event => {
    if (state.eventSource !== stream || resyncing) return;
    try {
      const data = JSON.parse(event.data);
      if (state.projected) applyViewDelta(data); else applyEvent(data);
    } catch { void resyncAndConnect(); }
  });
  stream.addEventListener("reset", () => { if (state.eventSource === stream) void resyncAndConnect(); });
  stream.onerror = () => {
    stream.close();
    if (state.eventSource === stream) state.reconnectTimer = setTimeout(resyncAndConnect, 1500);
  };
}
function applyViewDelta(delta) {
  if (delta.epoch !== state.epoch) { void resyncAndConnect(); return; }
  if (delta.sequence <= state.sequence) return;
  if (delta.previousSequence !== state.sequence || delta.sequence !== state.sequence + 1) { void resyncAndConnect(); return; }
  // Entire admissions/removals are reconciled together: never expose a partial island.
  for (const id of delta.removeEdgeIds) collectedView.apply("EDGE_REMOVE", {relationId:id});
  for (const id of delta.removeNodeIds) collectedView.apply("NODE_REMOVE", {personId:id});
  for (const node of delta.upsertNodes) collectedView.apply("NODE_UPSERT", node);
  for (const edge of delta.upsertEdges) collectedView.apply("EDGE_UPSERT", edge);
  state.sequence = delta.sequence;
  state.collectedPeople = delta.collected.people;
  state.collectedRelations = delta.collected.relations;
  if (viewTimer === null) viewTimer = setTimeout(flushView, 80);
}

function applyFilters() {
  discovery?.invalidate();
  renderer.setSetting("enableEdgeEvents", !state.pathIds.length);
  if (pathsDirty && state.pathIds.length) refreshPaths();
  const spotlight = discovery?.state();
  renderer.setSetting("nodeReducer", (node, data) => {
    if (!state.layout?.ready || (explorer && !explorer.visible(node))) return { ...data, hidden: true };
    if (state.pathIds.length) {
      // Sigma uses ONE / ONE_MINUS_SRC_ALPHA: supply premultiplied RGB.
      const endpoint = state.pathIds.includes(node), mask = pathResult.nodes.get(node) || 0;
      return { ...data, hidden: false, color: endpoint ? "#f2efe8" : mask ? pathColor(mask) : "rgba(38,41,40,0.28)",
        size: endpoint ? (mobileViewport.matches ? 5 : 8) : mask ? (mobileViewport.matches ? 2.8 : 4.5) : (mobileViewport.matches ? 1 : 2),
        zIndex: endpoint ? 3 : mask ? 2 : 0, highlighted: endpoint || node === state.selectedNode };
    }
    if (spotlight?.active) {
      const lit = spotlight.nodes.has(node);
      const base = mobileViewport.matches ? mobileNodeSize(data.size, lit) : data.size * layoutSettings.displayScale;
      return { ...data, hidden: false, color: lit ? spotlight.color : "#59615e",
        size: lit ? base * 1.34 : base, zIndex: lit ? 5 : 0, highlighted: lit };
    }
    const filterDomain = [...state.domains].find(domain => data.careerDomains?.includes(domain));
    const filterActive = state.domains.size > 0;
    const selected = state.selectedNode;
    const edgeEndpoint = state.selectedEdge && graph.hasEdge(state.selectedEdge) && graph.extremities(state.selectedEdge).includes(node);
    const unrelated = selected && node !== selected && (!graph.hasNode(selected) || !graph.areNeighbors(node, selected));
    const hoverQuiet = hoverNode && !selected && node !== hoverNode && !hoverNeighbors.has(node);
    const color = node === selected || edgeEndpoint ? "#f2efe8"
      : unrelated ? "#2b3231"
        : hoverNode && node === hoverNode ? "#d9e0dd"
          : hoverQuiet ? "#545c59"
        : filterDomain ? domainColors[filterDomain]
          : filterActive ? "#3d4644" : "#899d96";
    return { ...data, atlasNeighborhood: Boolean((selected && !unrelated) || (hoverNode && hoverNeighbors.has(node))), hidden: false, color, filterDomain: filterDomain || null,
      size: Math.min(mobileViewport.matches ? 2.3 : 2.8, (mobileViewport.matches ? .65 : .8) + Math.sqrt(data.size) * .32) * Math.min(1.4, Math.max(.7, layoutSettings.displayScale)) + (node === selected || edgeEndpoint ? 1.3 : 0), zIndex: node === selected || filterDomain ? 2 : 0,
      highlighted: node === selected || edgeEndpoint || Boolean(data.dragging) || (hoverNode === node && !mobileViewport.matches) };
  });
  renderer.setSetting("edgeReducer", (edge, data) => {
    if (!state.layout?.ready || (explorer && graph.extremities(edge).some(id=>!explorer.visible(id)))) return { ...data, hidden: true };
    if (state.pathIds.length) {
      const mask = pathResult.edges.get(edge) || 0;
      return { ...data, hidden: false, color: "rgba(0,0,0,0)",
        size: mask ? (mobileViewport.matches ? 1.8 : 2.5) : (mobileViewport.matches ? .25 : .6), zIndex: mask ? 2 : 0 };
    }
    if (spotlight?.active) {
      const lit = spotlight.edges.has(edge);
      return { ...data, type: lit ? "focus" : "line", hidden: false, color: lit ? spotlight.color : "#3a4240",
        size: lit ? (mobileViewport.matches ? 2.2 : Math.max(2, data.size * layoutSettings.displayScale)) : (mobileViewport.matches ? .35 : .7),
        zIndex: lit ? 4 : 0 };
    }
    const [source, target] = graph.extremities(edge);
    const typeHidden = state.relationType !== "ALL" && data.relationType !== state.relationType;
    const unrelated = state.selectedNode && source !== state.selectedNode && target !== state.selectedNode;
    const touchesActiveDomain = state.domains.size === 0 || [source, target].some(node =>
      graph.getNodeAttribute(node, "careerDomains")?.some(domain => state.domains.has(domain)));
    const hoverQuiet = hoverNode && !state.selectedNode && !hoverEdges.has(edge);
    const focus = edge === state.selectedEdge || (state.selectedNode && !unrelated) || (!state.selectedNode && hoverEdges.has(edge));
    return { ...data, type: focus ? "focus" : "line", hidden: typeHidden, color: focus ? "#b5c1b9" : unrelated || !touchesActiveDomain ? "#252b2a" : hoverQuiet ? "#38413e" : hoverNode && hoverEdges.has(edge) ? "#99a6a1" : "#58635f",
      size: mobileViewport.matches ? mobileEdgeSize(edge === state.selectedEdge) : Math.min(1.1, data.size * layoutSettings.displayScale * .22) + (edge === state.selectedEdge ? 1.5 : 0) };
  });
  renderer.refresh();
  updateUrl();
  refreshPersonConnections();
}

function inferCareerDomains(person) {
  const domains = new Set();
  if (domainColors[person.domain]) domains.add(person.domain);
  for (const profession of person.professions || []) {
    const label = (professionLabels.get(profession) || (/^Q[1-9][0-9]*$/.test(profession) ? "" : profession))
      .toLocaleLowerCase("fr").replaceAll("'", "’");
    for (const [domain, rule] of Object.entries(professionDomainRules)) {
      if (rule.test(label)) domains.add(domain);
    }
  }
  return [...domains];
}

async function hydrateCareerDomains(people) {
  const professions = [...new Set(people.flatMap(person => person.professions || []))];
  await loadProfessionLabels(professions);
  for (const person of people) {
    if (graph.hasNode(person.id)) graph.setNodeAttribute(person.id, "careerDomains", inferCareerDomains(person));
  }
  state.careerLabelsReady = true;
  applyFilters();
}

async function loadProfessionLabels(professions) {
  const missing = professions.filter(value => /^Q[1-9][0-9]*$/.test(value) && !professionLabels.has(value));
  for (let start = 0; start < missing.length; start += 50) {
    const ids = [...new Set(missing.slice(start, start + 50))];
    if (!ids.length) continue;
    try {
      const response = await fetch(`api/v1/wikidata/labels?ids=${encodeURIComponent(ids.join(","))}`);
      if (!response.ok) continue;
      const labels = await response.json();
      Object.entries(labels).forEach(([qid, label]) => professionLabels.set(qid, label));
    } catch { /* The factual dates and source remain usable if labels are temporarily unavailable. */ }
  }
}

function formatProfessions(professions) {
  if (!professions?.length) return "À documenter";
  const labels = professions.map(value => professionLabels.get(value) || (/^Q[1-9][0-9]*$/.test(value) ? "Chargement…" : value))
    .filter((value, index, values) => values.indexOf(value) === index);
  const visible = labels.slice(0, 5).join(", ");
  return labels.length > 5 ? `${visible} et ${labels.length - 5} autre${labels.length - 5 > 1 ? "s" : ""}` : visible;
}

function renderPerson(person) {
  const life = person.birthYear == null ? "Naissance à résoudre" : `Né·e en ${escapeHtml(person.birthYear)}`;
  const death = person.deathYear == null ? "" : `, décédé·e en ${escapeHtml(person.deathYear)}`;
  document.querySelector("#inspectorKicker").textContent = person.domain === "RELATION"
    ? "Personne reliée" : `${person.domain.toLowerCase()} · personnalité du périmètre`;
  document.querySelector("#inspectorTitle").textContent = person.name;
  document.querySelector("#inspectorBody").innerHTML = `<p class="person-summary">${life}${death}. ${escapeHtml(formatProfessions(person.professions))}.</p>
    <dl class="fact-list">
      <div><dt>Naissance</dt><dd>${escapeHtml(person.birthYear ?? "À résoudre")}</dd></div>
      ${person.deathYear ? `<div><dt>Décès</dt><dd>${escapeHtml(person.deathYear)}</dd></div>` : ""}
      <div><dt>Profession(s)</dt><dd>${escapeHtml(formatProfessions(person.professions))}</dd></div>
      <div><dt>Source</dt><dd><a href="${escapeAttribute(person.wikipediaUrl)}" target="_blank" rel="noreferrer">Article Wikipédia ↗</a></dd></div>
    </dl>
    <section id="personPortrait" aria-label="Portrait et crédits"></section>
    <section id="personConnections" class="person-connections" aria-label="Personnes liées"></section>`;
  refreshPortraitCredit();
  refreshPersonConnections();
}

function refreshPortraitCredit() {
  const target = document.querySelector("#personPortrait");
  if (target && state.selectedNode && graph.hasNode(state.selectedNode)) renderPortraitCredit(target,labelAudit.portraits.get(graph.getNodeAttribute(state.selectedNode,"wikidataId")),{compact:true});
}

function refreshRelationPortraits() {
  const target=document.querySelector("#relationPortraits");
  if (!target || !state.selectedEdge || !graph.hasEdge(state.selectedEdge)) return;
  const people=inspectorPortraitPeople(graph,state,true);
  const personHref=id=>{
    const url=new URL(serializeView(location.href,{...state,layoutSettings,camera:null}));
    url.searchParams.delete("edge");url.searchParams.set("person",id);return url.href;
  };
  renderRelationPortraits(target,people,qid=>labelAudit.portraits.get(qid),personHref);
}

function refreshPersonConnections() {
  const section = document.querySelector("#personConnections");
  if (!section || !state.selectedNode) return;
  const people = personConnections(graph, state.selectedNode);
  const base = new URL(serializeView(location.href, { ...state, layoutSettings, camera: null }));
  base.searchParams.delete("person");
  const edgeLink = (edge, text, className = "") => {
    base.searchParams.set("edge", edge.id);
    return `<a class="relation-connection ${className}" data-edge="${escapeAttribute(edge.id)}" href="${escapeAttribute(base.href)}">${escapeHtml(text)}</a>`;
  };
  const content = `<h2>Personnes liées <span>${people.length}</span></h2>
    ${people.length ? `<ul>${people.map(person =>
      `<li>${edgeLink(person.edges[0], person.name, "connection-name")}<span>${person.edges.map(edge => edgeLink(edge, edge.relation)).join(" · ")}</span></li>`
    ).join("")}</ul>` : `<p>Aucun lien dans le réseau collecté pour le moment.</p>`}`;
  // Avoid replacing focused links on unrelated live events.
  if (section.dataset.content !== content) { section.innerHTML = content; section.dataset.content = content; }
}

async function showPerson(id, { focus = true } = {}) {
  if (!graph.hasNode(id)) return;
  explorer?.beforePerson(id);
  state.selectedNode = id; state.selectedEdge = null;
  const person = graph.getNodeAttributes(id);
  renderPerson(person);
  document.querySelector("#inspector").hidden = false;
  if (mobileViewport.matches) document.querySelector("#inspector").scrollTop = 0;
  explorer?.afterPerson(focus);
  applyFilters();
  await loadProfessionLabels(person.professions || []);
  if (state.selectedNode === id) renderPerson(person);
}

function describeRelation(id){
  const [a,b]=graph.extremities(id),e=graph.getEdgeAttributes(id);
  return `${graph.getNodeAttribute(a,"label")} — ${relationLabel({type:e.relationType,temporalStatus:e.temporalStatus})} — ${graph.getNodeAttribute(b,"label")}`;
}
let relationReturnPerson=null;
function focusInspector(){const title=document.querySelector("#inspectorTitle");title.tabIndex=-1;title.focus({preventScroll:true});}
async function showEdge(id) {
  if (!graph.hasEdge(id)) return;
  explorer?.beforeEdge(id);
  if (!graph.hasEdge(id)) return;
  state.selectedEdge = id; state.selectedNode = null;
  const edge = graph.getEdgeAttributes(id);
  const [source, target] = graph.extremities(id);
  const sourceName = graph.getNodeAttribute(source, "label");
  const targetName = graph.getNodeAttribute(target, "label");
  const edgeLabel = relationLabel({ type: edge.relationType, temporalStatus: edge.temporalStatus });
  const statement = edge.relationType === "ADOPTIVE_PARENT_OF" ? `${sourceName} est parent adoptif de ${targetName}.`
    : edge.relationType === "STEP_PARENT_OF" ? `${sourceName} est ou a été beau-parent de ${targetName} (conjoint ou partenaire d’un parent).`
    : edge.relationType === "CO_PARENT" ? `${sourceName} et ${targetName} sont parents d’un même enfant.`
    : edge.relationType === "PARENT_OF" ? `${sourceName} est parent de ${targetName}.`
    : edge.relationType === "MENTOR_OF" ? `${sourceName} est mentor de ${targetName}.`
      : `${sourceName} et ${targetName} : ${edgeLabel.toLowerCase()}.`;
  document.querySelector("#inspectorKicker").textContent = "Relation documentée";
  document.querySelector("#inspectorTitle").textContent = `${sourceName} — ${targetName}`;
  const body = document.querySelector("#inspectorBody");
  body.innerHTML = `${relationReturnPerson ? `<button type="button" class="relation-back" data-return-person="${escapeAttribute(relationReturnPerson)}">← Relations de cette personne</button>` : ""}<p class="relation-statement">${escapeHtml(statement)}</p>
    <p class="relation-definition">${escapeHtml(relationDescriptions[edge.relationType] || "Relation explicitement documentée dans les sources collectées.")}</p>
    <section id="relationPortraits" class="relation-portraits" aria-label="Personnes de cette relation" hidden></section>
    <dl class="fact-list relation-facts">
      <div><dt>Nature</dt><dd><strong>${escapeHtml(edgeLabel)}</strong></dd></div>
      ${edge.detail ? `<div><dt>Précision</dt><dd>${escapeHtml(edge.detail)}</dd></div>` : ""}
      ${edge.commonObject ? `<div><dt>Objet commun</dt><dd>${escapeHtml(edge.commonObject)}</dd></div>` : ""}
      ${edge.workTitle ? `<div><dt>Œuvre</dt><dd>${escapeHtml(edge.workTitle)}</dd></div>` : ""}
      ${edge.startYear || edge.endYear ? `<div><dt>Période</dt><dd>${escapeHtml([edge.startYear, edge.endYear].filter(Boolean).join("–"))}</dd></div>` : ""}
      <div><dt>Temporalité</dt><dd>${escapeHtml(temporalLabels[edge.temporalStatus] || "Non précisée")}</dd></div>
      <div><dt>Méthode</dt><dd>${escapeHtml(extractionLabels[edge.extractionMethod] || edge.extractionMethod)}</dd></div>
      <div><dt>Preuves</dt><dd>${escapeHtml(edge.evidenceCount || 0)}</dd></div>
    </dl>
    <section id="relationProofs"><h2 class="proof-title">Passages sources</h2><p class="proof"><small>Chargement des preuves…</small></p></section>`;
  document.querySelector("#inspector").hidden = false;
  document.querySelector("#inspector").scrollTop = 0;
  refreshRelationPortraits();
  explorer?.afterEdge();
  applyFilters();
  const response = await fetch(`api/v1/relations/${encodeURIComponent(id)}/evidence`);
  if (state.selectedEdge !== id) return;
  if (!response.ok) {
    const loading = body.querySelector(".proof");
    if (loading) loading.innerHTML = `<small>Aucune preuve publique disponible.</small>`;
    return;
  }
  const proofs = await response.json();
  if (state.selectedEdge !== id) return;
  body.querySelector("#relationProofs").innerHTML = `<h2 class="proof-title">Passages sources</h2>${proofs.map(proof => `<article class="proof"><small>${escapeHtml(proof.provenanceType)} · révision ${escapeHtml(proof.sourceRevision)}${proof.section ? ` · ${escapeHtml(proof.section)}` : ""}</small>
    <blockquote>${escapeHtml(proof.exactExcerpt)}</blockquote><a href="${escapeAttribute(proof.sourceUrl)}" target="_blank" rel="noreferrer">Voir la source ↗</a></article>`).join("")}`;
}

const crawlStages = {
  WAITING_WIKIMEDIA: "attente demandée par Wikimedia", IDENTITY: "identification", DOWNLOAD: "téléchargement Wikipédia", EXTRACT: "lecture de la page",
  LINK_IDENTITIES: "résolution des liens Wikidata", PREPARE: "préparation des preuves",
  FAMILY_IDENTITIES: "vérification des liens familiaux", READY: "prête, en attente",
  PUBLISH: "publication des données", JEV: "analyse des relations par Jev", SAVE: "enregistrement"
};
function activityText(page) {
  return `${page.title} — ${crawlStages[page.stage] || "traitement"} · ${page.elapsedSeconds} s écoulées · ${page.wikimediaRequests} réponses Wikimedia`;
}
function setText(selector, text) {
  const element = document.querySelector(selector);
  if (element.textContent !== text) element.textContent = text;
}
function updateCounts(status) {
  setText("#peopleCount", status.people.toLocaleString("fr-FR"));
  setText("#relationCount", status.relations.toLocaleString("fr-FR"));
  setText("#pageCount", status.processedPages.toLocaleString("fr-FR"));
  const labels = { RUNNING: "crawl en cours", IDLE: "en attente", STOPPED: "crawl arrêté",
    FRONTIER_EXHAUSTED: "frontière épuisée", BUDGET_EXHAUSTED: "budget épuisé" };
  setText("#crawlState", labels[status.state] || status.state.toLowerCase());
  document.querySelector(".crawl-state").title = labels[status.state] || status.state.toLowerCase();
  document.querySelector("#crawlDot").classList.toggle("is-running", status.state === "RUNNING");
  const active = status.activePages || [];
  const current = active.find(page => !page.prefetched);
  const next = active.find(page => page.prefetched);
  const currentElement = document.querySelector("#crawlCurrent");
  const nextElement = document.querySelector("#crawlPrefetch");
  currentElement.textContent = current ? activityText(current)
    : status.state === "RUNNING" ? (status.seedDiscovery?.phase === "SEARCHING"
      ? "Recherche de nouvelles biographies éligibles…"
      : status.seedDiscovery && status.queuedPages === 0 && ["WAITING", "RETRY"].includes(status.seedDiscovery.phase)
      ? `Recherche de nouvelles graines — reprise automatique à ${new Date(status.seedDiscovery.nextAttemptAt).toLocaleTimeString("fr-FR")}`
      : status.queueRetry?.retryAt
      ? `Pages temporairement reportées — reprise automatique dans ${status.queueRetry.secondsRemaining} s (${status.queueRetry.title})`
      : "En attente de la prochaine page")
    : labels[status.state] || "Aucune page en cours";
  currentElement.title = currentElement.textContent;
  nextElement.hidden = !next;
  nextElement.textContent = next ? `Préparation en avance : ${activityText(next)}` : "";
  nextElement.title = nextElement.textContent;
}

async function pollStatus() {
  try {
    const response = await fetch("api/v1/status", { cache: "no-store", signal: AbortSignal.timeout(8000) });
    if (!response.ok) throw new Error("Status unavailable");
    updateCounts(await response.json());
  } catch {
    document.querySelector("#crawlCurrent").textContent = "Statut momentanément indisponible — reconnexion…";
    document.querySelector("#crawlPrefetch").hidden = true;
    document.querySelector("#crawlState").textContent = "statut indisponible";
    document.querySelector(".crawl-state").title = "statut indisponible";
    document.querySelector("#crawlDot").classList.remove("is-running");
  } finally {
    // No overlapping requests or stale responses if the server is temporarily slow.
    setTimeout(pollStatus, 2000);
  }
}

function updateSearchList() {
  const options = graph.mapNodes((id, data) => `<option value="${escapeAttribute(data.label)}" data-id="${escapeAttribute(id)}"></option>`);
  document.querySelector("#peopleList").innerHTML = options.join("");
  document.dispatchEvent(new Event("entourages:people-updated"));
}

function searchPerson() {
  const query = document.querySelector("#personSearch").value.trim().toLocaleLowerCase("fr");
  if (!query) return;
  const match = graph.findNode((id, data) => data.label.toLocaleLowerCase("fr") === query)
    || graph.findNode((id, data) => data.label.toLocaleLowerCase("fr").includes(query));
  if (match) showPerson(match); else showToast("Aucune personne trouvée dans le réseau collecté");
}

function updateUrl() {
  clearTimeout(urlTimer);
  if (!viewReady) return location.href;
  const url = (explorer?.serialize(new URL(serializeView(location.href, { ...state, layoutSettings, camera: renderer.getCamera().getState() }))) || new URL(serializeView(location.href, { ...state, layoutSettings, camera: renderer.getCamera().getState() }))).href;
  if (url !== location.href) history.replaceState(null, "", url);
  return url;
}

function restoreViewFromUrl() {
  const view = parseView(initialView, { domains: Object.keys(domainColors).filter(domain => domain !== "RELATION"), relations: Object.keys(relationLabels), fallbackSettings: layoutSettings });
  state.domains = new Set(view.domains);
  state.relationType = view.relationType;
  document.querySelector("#relationFilter").value = state.relationType;
  document.querySelectorAll(".domain").forEach(button => {
    const on = state.domains.has(button.dataset.domain); button.classList.toggle("is-on", on); button.setAttribute("aria-pressed", on);
  });
  if (view.pathIds.length) pathSearch.setIds(view.pathIds);
  if (view.selectedNode && graph.hasNode(view.selectedNode)) void showPerson(view.selectedNode, { focus: !view.camera });
  else if (view.selectedEdge && graph.hasEdge(view.selectedEdge)) void showEdge(view.selectedEdge);
  else applyFilters();
  // Apply after initial layout fitting and without a selection-focus animation.
  if (view.camera) renderer.getCamera().setState(view.camera);
  viewReady = true;
  document.querySelector("#shareViewButton").disabled = false;
  document.querySelectorAll(".path-trigger").forEach(button => { button.disabled = false; });
  updateUrl();
}

function showToast(message) {
  const toast = document.querySelector("#toast"); toast.textContent = message; toast.classList.add("is-visible");
  clearTimeout(showToast.timer); showToast.timer = setTimeout(() => toast.classList.remove("is-visible"), 1800);
}
function escapeHtml(value) { return String(value).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;"); }
function escapeAttribute(value) { return escapeHtml(value).replaceAll('"', "&quot;"); }

const nodeDrag = installNodeDrag(renderer, graph, () => state.layout);
discovery = installDiscovery({ graph, renderer, container, mobile: mobileViewport, initialView,
  getState: () => state, refresh: applyFilters, labels: labelAudit, showEdge });
let pointerSpotlightNodes = [];
// Capture deliberate input before Sigma starts a gesture; the camera never fights a drag or zoom.
document.addEventListener("pointerdown", event => {
  if (!event.target.closest("#discoveryCard, #surpriseButton, #mobileSurpriseButton")) {
    pointerSpotlightNodes = container.contains(event.target) ? [...discovery.state().nodes] : [];
    discovery.takeControl();
  }
}, true);
container.addEventListener("wheel", () => discovery.takeControl(), { capture: true, passive: true });
document.addEventListener("keydown", event => {
  if (event.key !== "Escape" && event.target.closest?.("#discoveryCard")) return;
  if (event.key === "Escape" || event.key === "Enter" || event.key === " " || event.key.startsWith("Arrow")) discovery.takeControl();
}, true);
document.addEventListener("input", () => discovery.takeControl(), true);
document.addEventListener("change", () => discovery.takeControl(), true);
renderer.on("enterNode", ({ node }) => {
  if (mobileViewport.matches || state.pathIds.length || discovery.state().active || state.selectedNode) return;
  clearTimeout(hoverClearTimer);
  hoverNode = node; hoverNeighbors = new Set(graph.neighbors(node)); hoverEdges = new Set(graph.edges(node));
  renderer.refresh();
});
function releaseHover() {
  clearTimeout(hoverClearTimer);
  if (!hoverNode) return;
  hoverClearTimer = setTimeout(() => {
    // Keep the label and neighborhood stable while crossing the small gap
    // between a person mark and its clickable name.
    const data = hoverNode && renderer.getNodeDisplayData(hoverNode);
    const point = data && renderer.framedGraphToViewport(data);
    if (point && hoverPointer && Math.hypot(hoverPointer.x - point.x, hoverPointer.y - point.y) <= renderer.scaleSize(data.size) + 3) return;
    const label = labelAudit.boxes.find(box => box.id === hoverNode);
    if (label && hoverPointer && hoverPointer.x >= label.left && hoverPointer.x <= label.right && hoverPointer.y >= label.top && hoverPointer.y <= label.bottom) return;
    hoverNode = null; hoverNeighbors.clear(); hoverEdges.clear(); renderer.refresh();
  }, 120);
}
renderer.on("leaveNode", releaseHover);
container.addEventListener("pointermove", event => {
  const bounds = container.getBoundingClientRect();
  hoverPointer = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  if (hoverNode) releaseHover();
});
container.addEventListener("pointerleave", () => { hoverPointer = null; releaseHover(); });

// CSS-screen geometry mirrors the actual rendered batches, not raw topology.
function relationGeometry(){
  const {width,height}=renderer.getDimensions(),points=new Map();
  graph.forEachNode(id=>{const d=renderer.getNodeDisplayData(id);if(d&&!d.hidden)points.set(id,{id,...renderer.framedGraphToViewport(d),size:renderer.scaleSize(d.size)});});
  const local=p=>p&&p.x>=-width*.01&&p.x<=width*1.01&&p.y>=-height*.01&&p.y<=height*1.01;
  const edges=state.pathIds.length?labelAudit.curves.map(c=>({id:c.id,curve:c,endpoints:[c.a,c.b]})):graph.mapEdges((id)=>{
    const d=renderer.getEdgeDisplayData(id),[a,b]=graph.extremities(id),source=points.get(a),target=points.get(b);
    const hidden=!d||d.hidden||!source||!target||(d.type!=="focus"&&(semantic.style().edgeOpacity===0||(!local(source)&&!local(target))));
    return {id,source,target,hidden};
  });
  return {width,height,nodes:[...points.values()],edges};
}
function relationObstacles(){
  const origin=container.getBoundingClientRect();
  return [...document.querySelectorAll(".topbar,.toolrail,.mobile-graph-tools,.graph-controls,footer,#pathNotice,#inspector,#discoveryCard,#neighbourhoodControls,#mapControls,.explorer-bar,dialog[open]")].filter(el=>!el.hidden&&el.getClientRects().length).map(el=>{const b=el.getBoundingClientRect();return {left:b.left-origin.left,right:b.right-origin.left,top:b.top-origin.top,bottom:b.bottom-origin.top};});
}
const relationInteraction=installRelationInteraction({renderer,getGeometry:relationGeometry,
  getLabels:()=>({labels:labelAudit.boxes,edgeLabels:labelAudit.edgeBoxes}),getObstacles:relationObstacles,
  describe:describeRelation,onPerson:id=>{relationReturnPerson=null;void showPerson(id);},
  onRelation:id=>{relationReturnPerson=null;void showEdge(id);focusInspector();},
  isSuppressed:()=>nodeDrag.suppressClick(),mobile:()=>mobileViewport.matches});
renderer.on("kill",()=>relationInteraction.kill());
renderer.on("clickNode",({node,event})=>{if(!event){void showPerson(node);return;}relationInteraction.click(event,{fallbackNode:node});});
renderer.on("clickEdge",({edge,event})=>{if(!event){void showEdge(edge);return;}relationInteraction.click(event);});
renderer.on("clickStage",({event})=>{
  // A mobile discovery highlight can disappear on pointerdown; keep its person
  // target through this one gesture, without resurrecting a hidden relation.
  let fallbackNode=null;
  if(mobileViewport.matches)for(const id of pointerSpotlightNodes){if(graph.hasNode(id)){const p=renderer.graphToViewport(graph.getNodeAttributes(id));if(Math.hypot(event.x-p.x,event.y-p.y)<=22){fallbackNode=id;break;}}}
  pointerSpotlightNodes=[];
  if(!relationInteraction.click(event,{fallbackNode}))document.querySelector("#closeInspector").click();
});
mobileViewport.addEventListener("change", () => {
  discovery.dismiss("viewport");
  renderer.setSetting("minEdgeThickness", mobileViewport.matches ? .8 : 1);
  applyFilters();
});
document.querySelector("#inspectorBody").addEventListener("click", event => {
  const back=event.target.closest("[data-return-person]");
  if(back){const id=back.dataset.returnPerson;relationReturnPerson=null;void showPerson(id,{focus:false});focusInspector();return;}
  const edge=event.target.closest("a.relation-connection"),link=event.target.closest("a.person-connection");
  if((!link&&!edge)||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;
  event.preventDefault();
  if(edge){relationReturnPerson=state.selectedNode;void showEdge(edge.dataset.edge);}else{relationReturnPerson=null;void showPerson(link.dataset.person);}
  document.querySelector("#inspector").scrollTop=0;focusInspector();
});
document.querySelector("#searchButton").addEventListener("click", searchPerson);
document.querySelector("#personSearch").addEventListener("keydown", event => { if (event.key === "Enter") searchPerson(); });
document.querySelectorAll(".domain").forEach(button => button.addEventListener("click", () => {
  const domain = button.dataset.domain;
  if (state.domains.has(domain)) state.domains.delete(domain); else state.domains.add(domain);
  button.classList.toggle("is-on", state.domains.has(domain)); button.setAttribute("aria-pressed", state.domains.has(domain)); applyFilters();
}));
document.querySelector("#relationFilter").addEventListener("change", event => { state.relationType = event.target.value; applyFilters(); });
document.querySelector("#pauseButton").addEventListener("click", event => {
  state.paused = !state.paused; event.currentTarget.textContent = state.paused ? "Reprendre" : "Pause";
  event.currentTarget.setAttribute("aria-pressed", state.paused); state.layout?.setPaused(state.paused);
  document.documentElement.dataset.forceAtlasWorker = state.paused ? "paused" : "running";
  updateUrl();
});
document.querySelector("#recenterButton").addEventListener("click", () => {document.querySelector("#explorerTools")?.close();document.querySelector("#mapFit")?.click();});
document.addEventListener("keydown",event=>{if(event.key==="Escape"&&!document.querySelector("dialog[open]")&&!document.querySelector("#inspector").hidden)document.querySelector("#closeInspector").click();});
document.querySelector("#closeInspector").addEventListener("click", () => { document.querySelector("#inspector").hidden = true; relationReturnPerson=null; if(document.activeElement?.closest("#inspector"))container.focus({preventScroll:true}); state.selectedNode = null; state.selectedEdge = null; explorer?.clear(); applyFilters(); });
const pathSearch = installPathSearch(() => graph.mapNodes((id, data) => ({ id, name: data.label, birthYear: data.birthYear })), setPathIds, fitPaths);
installViewChrome();
installMobileSearch(() => graph.mapNodes((id, data) => ({ id, name: data.label, birthYear: data.birthYear })), id => { void showPerson(id); });
installShareView(updateUrl, showToast);
explorer = installAtlasExplorer({graph, renderer, semantic, communities, state, refresh:applyFilters, showPerson, showEdge, labelAudit,
  stopDiscovery:()=>discovery?.takeControl(),
  clearSelection:()=>{ state.selectedNode=null;state.selectedEdge=null;state.pathIds=[];pathSearch.setIds([]);document.querySelector("#inspector").hidden=true; },
  restoreSelection:previous=>{pathSearch.setIds(previous.pathIds);state.selectedNode=previous.selectedNode;state.selectedEdge=previous.selectedEdge;
    if(state.selectedNode&&graph.hasNode(state.selectedNode)){renderPerson(graph.getNodeAttributes(state.selectedNode));document.querySelector("#inspector").hidden=false;}
    else if(state.selectedEdge&&graph.hasEdge(state.selectedEdge))void showEdge(state.selectedEdge);
  }
});
document.addEventListener("entourages:person-path",event=>pathSearch.setIds([event.detail]));
renderer.getCamera().on("updated", () => {
  clearTimeout(urlTimer);
  if (viewReady) urlTimer = setTimeout(updateUrl, 160);
});
document.querySelector("#methodButton").addEventListener("click", () => document.querySelector("#methodDialog").showModal());
document.querySelector("#closeMethod").addEventListener("click", () => document.querySelector("#methodDialog").close());
if (state.paused) {
  document.querySelector("#pauseButton").textContent = "Reprendre";
  document.querySelector("#pauseButton").setAttribute("aria-pressed", "true");
}
installLayoutControls(layoutSettings, settings => {
  layoutSettings = settings;
  renderer.setSetting("labelSize", Math.max(10, 14 * Math.sqrt(settings.displayScale)));
  applyFilters();
  state.layout?.setSettings(settings);
}, () => state.paused);
window.addEventListener("pagehide", event => { nodeDrag.cancel(); discovery.takeControl(); if (!event.persisted) { discovery.destroy(); communities.kill(); state.layout?.kill(); state.eventSource?.close(); } });
setTimeout(pollStatus, 2000);
window.__entouragesAudit = Object.freeze({
  snapshot: () => ({ explorer:explorer?.snapshot(), projected: state.projected, epoch: state.epoch, warmStarted: Boolean(state.layout?.warmStarted), receivedNodes: collectedView.nodes.size, receivedEdges: collectedView.edges.size, collectedNodes: state.collectedPeople, collectedEdges: state.collectedRelations, minimumComponentSize: collectedView.minimum, largestComponentOnly: collectedView.largestOnly, hideEdgesOnMove: renderer.getSetting("hideEdgesOnMove"), draggedNode: state.layout?.dragged?.id || null, appliedDraggedNode: state.layout?.appliedDraggedNode, layoutSettings: { ...layoutSettings }, appliedLayoutSettings: state.layout?.appliedSettings, nodes: graph.order, edges: graph.size,
    workerRunning: Boolean(state.layout) && !state.paused, sequence: state.sequence,
    hoverNode, selectedNode: state.selectedNode, selectedEdge: state.selectedEdge, paused: state.paused, relationType: state.relationType, labelCollisionPasses: labelAudit.passes, layoutReady: Boolean(state.layout?.ready), layoutIterations: state.layout?.iterations || 0,
    discovery: discovery.state().active && { id: discovery.state().active.id, kind: discovery.state().active.kind,
      nodes: [...discovery.state().nodes], edges: [...discovery.state().edges] },
    edgeEventsEnabled: renderer.getSetting("enableEdgeEvents"), careerLabelsReady: Boolean(state.careerLabelsReady),
    pathIds: [...state.pathIds], pathPairs: pathResult.pairs, pathNodes: [...pathResult.nodes], pathEdges: [...pathResult.edges],
    activeDomains: [...state.domains],
    coloredCounts: Object.fromEntries(Object.keys(domainColors).map(domain => [domain,
      graph.filterNodes((_node, data) => data.careerDomains?.includes(domain)).length])),
    multiCareerNodes: graph.filterNodes((_node, data) => (data.careerDomains?.filter(domain => domain !== "RELATION").length || 0) > 1).length }),
  geometry: () => ({
    camera: renderer.getCamera().getState(), bbox: renderer.getCustomBBox(),
    curves: labelAudit.curves, labels: labelAudit.boxes, edgeLabels: labelAudit.edgeBoxes, labelZoom: labelAudit.zoom, dimensions: renderer.getDimensions(),
    nodes: graph.mapNodes((id, a) => ({ id, color: renderer.getNodeDisplayData(id).color, size: renderer.scaleSize(renderer.getNodeDisplayData(id).size), world: { x:a.x,y:a.y }, ...renderer.graphToViewport(a) })),
  }),
  relations: () => ({geometry:relationInteraction.geometry(),stats:relationInteraction.stats()}),
  relationTarget: (point,touch=false) => {const h=relationInteraction.resolve(point,touch);return {kind:h.kind,id:h.id,candidates:h.candidates?.map(c=>c.id)};},
  atlas: () => ({ ...semantic.style(), performance: performanceAudit.snapshot(), fog: communities.stats(), portraits:labelAudit.portraits.stats() }),
  setCamera: camera => renderer.getCamera().setState(camera),
  clickFirstEdge: () => {
    if (!graph.size) return false;
    renderer.emit("clickEdge", { edge: graph.edges()[0] });
    return true;
  },
});
resyncAndConnect();
